/**
 * RenderReviewPanel — V14.92 : notation ★ 1-5, refus « trop éloigné du plan »,
 * annotation libre, relance (même job + consigne corrective) ou annotation
 * visuelle (AnnotationDialog existant). Usage interne (jamais côté client).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, ShieldX, RotateCcw, Brush, Loader2, Undo2 } from 'lucide-react';
import { emitRefsChanged } from '../../lib/generationEvents';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

export const isRejected = (item) => item?.review_status === 'rejected';

const Stars = ({ value, onChange, disabled }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-0.5" role="radiogroup" aria-label="Note du rendu" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((n) => {
        const on = n <= (hover || value || 0);
        return (
          <button
            key={n} type="button" role="radio" aria-checked={value === n} disabled={disabled}
            onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
            onClick={() => onChange(value === n ? null : n)}
            data-testid={`review-star-${n}`}
            className="p-0.5 transition-transform hover:scale-110 disabled:opacity-40"
            title={`${n}/5`}
          >
            <Star className="w-4 h-4" style={{ color: on ? '#FACC15' : 'rgba(255,255,255,0.22)', fill: on ? '#FACC15' : 'transparent' }} />
          </button>
        );
      })}
      {value ? <span className="ml-1 text-[10.5px] tabular-nums" style={{ color: 'var(--c-text-2)' }}>{value}/5</span> : null}
    </div>
  );
};

const RenderReviewPanel = ({ item, projectId, onChanged, onRelaunched, onAnnotate, compact = false }) => {
  const [note, setNote] = useState(item?.review_annotation || '');
  const [busy, setBusy] = useState(false);
  const [relaunching, setRelaunching] = useState(false);
  useEffect(() => { setNote(item?.review_annotation || ''); }, [item?.job_id, item?.review_annotation]);

  if (!item || item.status !== 'completed') return null;
  const rejected = isRejected(item);

  const review = async (body, okMsg) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/projects/${projectId}/generations/${item.job_id}/review`, body, opts);
      const { job_id: _j, unanchored, ...patch } = r.data || {};
      onChanged?.(item.job_id, patch);
      if (body.status !== undefined) emitRefsChanged({ lotId: item.lot_id, stage: item.params?.render_stage, anchored: false, nextStage: null });
      if (okMsg) toast.success(okMsg, unanchored ? { description: 'La référence d\'étape a été retirée de ce rendu.' } : undefined);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  };

  const saveNote = () => {
    if ((note || '').trim() === (item.review_annotation || '').trim()) return;
    review({ annotation: note }, 'Annotation enregistrée.');
  };

  const relaunch = async () => {
    if ((note || '').trim() !== (item.review_annotation || '').trim()) await review({ annotation: note });
    setRelaunching(true);
    try {
      const r = await axios.post(`${API}/projects/${projectId}/generations/${item.job_id}/relaunch`, {}, opts);
      onChanged?.(item.job_id, { relaunch_job_id: r.data?.job_id, is_archived: true });
      toast.success('Rendu relancé — l\'ancien passe en Archives', { description: r.data?.correction_note ? `Consigne : « ${r.data.correction_note.slice(0, 80)} »` : 'Mêmes paramètres, masque re-rendu depuis le plan.' });
      onRelaunched?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Relance impossible.');
    } finally {
      setRelaunching(false);
    }
  };

  return (
    <div className={compact ? 'space-y-1.5' : 'px-4 py-3 space-y-2'} style={compact ? undefined : { borderBottom: '1px solid var(--c-border)' }}
         data-testid="render-review-panel" data-rejected={rejected ? 'true' : 'false'}>
      <div className="flex items-center justify-between gap-2">
        {!compact && <span className="hud-eyebrow">Notation</span>}
        <Stars value={item.review_rating || null} disabled={busy} onChange={(v) => review({ rating: v }, v ? `Note : ${v}/5` : 'Note retirée.')} />
      </div>

      <button
        type="button" disabled={busy}
        onClick={() => review({ status: rejected ? null : 'rejected' }, rejected ? 'Refus levé.' : 'Rendu refusé — trop éloigné du plan.')}
        data-testid="review-reject-btn"
        className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg font-semibold transition-colors disabled:opacity-50"
        style={rejected
          ? { background: 'rgba(239,68,68,0.22)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.55)' }
          : { background: 'rgba(255,255,255,0.04)', color: 'var(--c-text-2)', border: '1px solid var(--c-border)' }}
      >
        {rejected ? <Undo2 className="w-3.5 h-3.5" /> : <ShieldX className="w-3.5 h-3.5" />}
        {rejected ? 'Refusé — trop éloigné du plan · lever le refus' : 'Refuser — trop éloigné du plan'}
      </button>

      {Array.isArray(item?.fidelity_report?.issues) && item.fidelity_report.issues.length > 0 && (
        <div className="rounded-lg px-2.5 py-1.5 text-[10.5px]" style={{ background: 'rgba(252,165,165,0.06)', border: '1px solid rgba(252,165,165,0.25)', color: 'var(--c-text-2)' }} data-testid="review-judge-issues">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="font-semibold" style={{ color: '#FCA5A5' }}>
              Écarts relevés par le juge{item.fidelity_report?.scores?.furniture != null ? ` · mobilier ${item.fidelity_report.scores.furniture}%` : ''}
            </span>
            <button
              type="button"
              onClick={() => {
                const next = [note, ...item.fidelity_report.issues.map((i) => `Corriger : ${i}`)].filter(Boolean).join('\n');
                setNote(next);
                review({ annotation: next }, 'Consigne de correction enregistrée — « Relancer » pour régénérer.');
              }}
              className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
              style={{ background: 'rgba(252,165,165,0.15)', color: '#FCA5A5' }}
              data-testid="review-use-judge-issues"
            >
              Corriger d'après le juge
            </button>
          </div>
          <ul className="list-disc pl-4 space-y-0.5">
            {item.fidelity_report.issues.slice(0, 4).map((i, idx) => <li key={idx}>{i}</li>)}
          </ul>
        </div>
      )}

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={saveNote}
        rows={compact ? 2 : 3}
        maxLength={2000}
        placeholder="Annotation : ex. « escalier manquant à gauche », « mur du séjour trop court »…"
        data-testid="review-annotation-input"
        className="w-full text-[11px] px-2.5 py-1.5 rounded-lg resize-none focus:outline-none"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }}
      />

      <div className="flex items-center gap-1.5">
        <button
          type="button" onClick={relaunch} disabled={relaunching || busy}
          data-testid="review-relaunch-btn"
          className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg font-semibold disabled:opacity-50"
          style={rejected ? { background: 'var(--c-gold)', color: '#0F1115' } : { background: 'rgba(217,160,91,0.16)', color: 'var(--c-gold)', border: '1px solid rgba(217,160,91,0.4)' }}
          title="Recrée un rendu identique (mêmes paramètres, même étape, même ancre) en injectant l'annotation comme consigne corrective"
        >
          {relaunching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Relancer
        </button>
        {onAnnotate && (
          <button
            type="button" onClick={() => onAnnotate(item)}
            data-testid="review-annotate-visual-btn"
            className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg font-semibold"
            style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--c-text)', border: '1px solid var(--c-border)' }}
            title="Dessiner sur le rendu et demander une révision ciblée"
          >
            <Brush className="w-3.5 h-3.5" /> Annoter
          </button>
        )}
      </div>
      {item.relaunch_job_id && (
        <p className="text-[10px]" style={{ color: 'var(--c-text-faint)' }} data-testid="review-relaunched-info">
          Relancé → rendu {item.relaunch_job_id.replace('gen_', '').slice(0, 6)}
        </p>
      )}
      {item.relaunched_from_job_id && (
        <p className="text-[10px]" style={{ color: 'var(--c-text-faint)' }} data-testid="review-relaunched-from-info">
          Relance du rendu {item.relaunched_from_job_id.replace('gen_', '').slice(0, 6)}{item.params?.correction_note ? ` · « ${item.params.correction_note.slice(0, 60)} »` : ''}
        </p>
      )}
    </div>
  );
};

export default RenderReviewPanel;
